/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import classNames from 'classnames';
import gql from 'graphql-tag';
import { compact } from 'lodash';
import moment from 'moment';
import { compose } from 'recompose';

import { FormattedMessage } from 'js/lib/coursera.react-intl';
import mapProps from 'js/lib/mapProps';
import { formatDateTimeDisplay } from 'js/utils/DateTimeUtils';

import { Typography, breakpoints } from '@coursera/cds-core';

import { renderToggleButtonContentWithContext } from 'bundles/enterprise-legacy-xdp/components/aboutXrenderFuncs';
import withFragments from 'bundles/graphql/components/withFragments';
import TogglableContent from 'bundles/page/components/TogglableContent';
import Card from 'bundles/partners-graphql/components/Card';
import type {
  PartnerPageQuery_PartnersV1Resource_shortName_partners_courses_elements as CourseData,
  PartnerPageQuery_PartnersV1Resource_shortName_partners_courses as Courses,
} from 'bundles/partners-graphql/components/__generated__/PartnerPageQuery';
import { getLink, isOnDemand, sortCourses } from 'bundles/partners-graphql/utils/courseUtils';

import _t from 'i18n!nls/partners-graphql';

type PropsFromCaller = {
  title: string;
  partnerShortName: string;
  courses: Courses | null;
};

type SanitizedCourses = {
  courses: CourseData[];
};

type PropsToComponent = Omit<PropsFromCaller, 'courses'> & SanitizedCourses;

const styles = {
  courseHeader: css`
    margin: var(--cds-spacing-200) !important;
  `,
  togglableContent: css`
    .collapse-button {
      margin-left: var(--cds-spacing-200) !important;
    }
  `,
  cardsContainer: css`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(25%, 1fr));
    grid-row-gap: var(--cds-spacing-400);

    ${breakpoints.down('xs')} {
      grid-template-columns: repeat(auto-fit, minmax(50%, 1fr));
    }
  `,
};

const formatDateMessage = (upcomingSessionStartDate: number) => {
  const now = moment();
  const momentStartDate = moment(upcomingSessionStartDate);
  const startDate = formatDateTimeDisplay(upcomingSessionStartDate, 'MMM DD, YYYY');

  return now > momentStartDate ? (
    <FormattedMessage message={_t('Started {startDate}')} startDate={startDate} />
  ) : (
    <FormattedMessage message={_t('Starts {startDate}')} startDate={startDate} />
  );
};

const getFormattedStartDateForStanford = (course: CourseData) => {
  let startDateMessage;
  if (course.courseStatus === 'preenroll') {
    const startDate = course.plannedLaunchDate;
    startDateMessage = <FormattedMessage message={_t('Starting {startDate}')} startDate={startDate} />;
  } else if (course.courseStatus === 'launched') {
    // Since Stanford isn't using sessions v2, we need to first check for the upcoming session start date to show "Started/Starts ..."
    if (course.upcomingSessionStartDate) {
      startDateMessage = formatDateMessage(course.upcomingSessionStartDate);
    } else if (isOnDemand(course)) {
      startDateMessage = _t('Available now');
    }
  }

  return startDateMessage;
};

const getFormattedStartDate = (course: CourseData, partnerShortName: string) => {
  let startDateMessage;

  if (partnerShortName === 'stanford') {
    startDateMessage = getFormattedStartDateForStanford(course);
  } else if (course.courseStatus === 'preenroll') {
    const startDate = course.plannedLaunchDate;
    startDateMessage = <FormattedMessage message={_t('Starting {startDate}')} startDate={startDate} />;
  } else if (course.courseStatus === 'launched') {
    if (isOnDemand(course)) {
      startDateMessage = _t('Available now');
    } else if (course.upcomingSessionStartDate) {
      startDateMessage = formatDateMessage(course.upcomingSessionStartDate);
    }
  }

  return startDateMessage;
};

const CourseCards = ({ courses, title, partnerShortName }: PropsToComponent) => {
  const sortedCourses = sortCourses(courses);

  return (
    <div className="bt3-row">
      <div className="bt3-col-md-12">
        {title && (
          <Typography variant="h2" component="h3" css={styles.courseHeader}>
            {title}
          </Typography>
        )}
        <TogglableContent
          css={styles.togglableContent}
          collapsedHeight={340}
          disableCollapseScroll={true}
          hideShowLessBtn={true}
          renderToggleButtonContentFunction={renderToggleButtonContentWithContext(_t('Show All'))}
          toggleButtonClassName={classNames('bold m-t-1', 'collapse-button')}
          toggleButtonType="button"
          trackingName="show-all-partner-courses"
          useSmartCollapse
        >
          <div css={styles.cardsContainer}>
            {sortedCourses.map((course) => (
              <Card
                key={course.id}
                slug={getLink(course)}
                name={course.name}
                logo={course.photoUrl}
                availability={getFormattedStartDate(course, partnerShortName)}
              />
            ))}
          </div>
        </TogglableContent>
      </div>
    </div>
  );
};

export const coursesFragment = gql`
  fragment CoursesFragment on CoursesV1Connection {
    elements {
      id
      name
      startDate
      courseType
      slug
      courseStatus
      primaryLanguages
      photoUrl
      upcomingSessionStartDate
      plannedLaunchDate
      s12nIds
    }
  }
`;

export default compose<PropsToComponent, PropsFromCaller>(
  withFragments({
    courses: coursesFragment,
  }),
  mapProps<PropsToComponent, PropsFromCaller>(({ courses, ...rest }) => ({
    courses: compact(courses?.elements),
    ...rest,
  }))
)(CourseCards);
